/**
 * Module dependencies.
 */

import { ifProp, switchProp } from 'styled-tools';
import { media } from 'src/styles/media';
import { useRouter } from 'next/router';
import Loading from 'src/components/core/loading';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import useBodyScroll from 'src/hooks/use-body-scroll';

/**
 * `Animation` type.
 */

type Animation = 'start' | 'end';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ visible: boolean }>`
  overflow: hidden;
  transition: opacity var(--transition-default);

  ${ifProp(
    'visible',
    css`
      opacity: 1;
      transition-delay: 0s;
    `,
    css`
      opacity: 0;
      transition-delay: 0.6s;
    `
  )}
`;

/**
 * `AnimatedBackground` styled component.
 */

const AnimatedBackground = styled.div<{ animation?: Animation }>`
  align-items: center;
  animation-duration: 0.6s;
  animation-fill-mode: both;
  animation-timing-function: cubic-bezier(0.23, 1, 0.32, 1);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 100;

  ${media.min.md`
    background-attachment: fixed;
    background-position: top center;
    background-repeat: no-repeat;
    background-size: 100% auto;
  `}

  ${switchProp('animation', {
    end: css`
      animation-name: fadeOut;
      pointer-events: none;
    `,
    start: css`
      animation-name: fadeIn;
      pointer-events: all;
    `
  })}
`;

/**
 * `PageTransition` component.
 */

const PageTransition = ({ children }: Props): ReactElement => {
  const router = useRouter();
  const [animation, setAnimation] = useState<Animation>();

  useEffect(() => {
    setAnimation('end');
  }, []);

  useEffect(() => {
    const handleRouteChangeStart = ({ shallow }: any) => {
      if (shallow) {
        return;
      }

      setAnimation('start');
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.events, router.pathname]);

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      setAnimation('end');
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.on('routeChangeError', handleRouteChangeComplete);
    };
  }, [router.events]);

  useBodyScroll({ off: animation === 'start' });

  return (
    <>
      <AnimatedBackground animation={animation}>
        <Loading active={animation === 'start'} />
      </AnimatedBackground>

      <Wrapper visible={animation === 'end'}>{children}</Wrapper>
    </>
  );
};

/**
 * Export `PageTransition` component.
 */

export default PageTransition;
